import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ApiOutlined,
  CheckCircleOutlined,
  DatabaseOutlined,
  EditOutlined,
  LockOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  StarOutlined,
} from '@ant-design/icons';

import { DataTable, MenuThaoTac, PageHeader, Tile, TileBody, TileHeader } from '../../../components/shared';
import { Badge, Button, Checkbox, Dialog, Empty, Field, Input, Skeleton, Textarea } from '../../../components/ui';
import { useDuongDan } from '../../../hooks/useDuongDan';
import { useModulePermissions, usePermission } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { ngayGioVN, soVN } from '../../../lib/dinh-dang';
import { loiTheoTruong } from '../../../lib/errors';
import { useToast } from '../../../lib/toast';
import { HopXacNhanThaoTac } from '../../nguoi-dung/components/HopXacNhanThaoTac';
import { LoiTrongHop } from '../../tenant/components/phan-form';
import { DaiBao } from '../components/DaiBao';
import { BADGE_MAC_DINH, PHAN_HE_KHOA_API, QUYEN, TRANG_THAI_NGUON } from '../constants';
import { useLoiBaoCao } from '../hooks/useBaoCao';
import { useDanhSachNguon, useDatNguonMacDinh, useDatTrangThaiNguon, useSuaNguon, useTaoNguon } from '../hooks/useNguon';
import { maNguonDuyNhat } from '../nghiep-vu/ma-nguon';
import type { Source } from '../types';

type Hop =
  | { loai: 'nguon'; goc?: Source }
  | { loai: 'mac-dinh'; nguon: Source }
  | { loai: 'ngung'; nguon: Source }
  | null;

/** Gốc API công khai của module (ADR-0068: hệ thống ngoài gọi qua Gateway bằng `X-Api-Key`). */
const GOC_CONG_KHAI = '/api/v1/m/bao-cao/public';

/**
 * Nguồn dữ liệu (`/bao-cao-doanh-nghiep/nguon`): mỗi nguồn giữ bộ số liệu và tài liệu riêng; app di động
 * đọc nguồn mặc định. Không có xoá nguồn (server không mở `DELETE`) — ngừng hoạt động thay cho xoá.
 * Khối "Tích hợp hệ thống ngoài" thay màn khoá API riêng của native: khoá do màn Khoá API của tổ chức cấp.
 */
export function NguonView() {
  const { t, locale } = useT();
  const toast = useToast();
  const dichLoi = useLoiBaoCao();
  const quyen = useModulePermissions(QUYEN);
  const ds = useDanhSachNguon(quyen.nguonDoc);
  const datMacDinh = useDatNguonMacDinh();
  const datTrangThai = useDatTrangThaiNguon();
  const [hop, setHop] = useState<Hop>(null);
  const dongHop = () => setHop(null);

  const tieuDe = t('nav.muc.nguonBaoCao');
  const nguon = ds.data ?? [];

  const batLai = (n: Source) =>
    datTrangThai.mutate(
      { id: n.id, bat: true },
      {
        onSuccess: () => toast.success(t('baoCaoDoanhNghiep.nguon.daBatLai', { ten: n.name })),
        onError: (e) => toast.error(dichLoi(e)),
      },
    );

  return (
    <div className="space-y-4 p-4 lg:p-6">
      <PageHeader
        breadcrumb={[{ label: t('nav.nhom.baoCaoDoanhNghiep') }, { label: tieuDe }]}
        titleIcon={<DatabaseOutlined className="text-warning-600" />}
        title={tieuDe}
        subtitle={t('baoCaoDoanhNghiep.nguon.moTa')}
        actions={
          quyen.nguonDoc && quyen.nguonTao ? (
            <Button variant="primary" size="md" icon={<PlusOutlined />} onClick={() => setHop({ loai: 'nguon' })}>
              {t('baoCaoDoanhNghiep.nguon.them')}
            </Button>
          ) : undefined
        }
      />

      {!quyen.nguonDoc ? (
        <Tile>
          <Empty
            icon={LockOutlined}
            title={t('baoCaoDoanhNghiep.khongCoQuyen.tieuDe')}
            description={t('baoCaoDoanhNghiep.khongCoQuyen.moTa', { quyen: QUYEN.nguonDoc })}
          />
        </Tile>
      ) : ds.isError ? (
        <DaiBao
          tong="loi"
          hanhDong={
            <Button variant="outline" size="sm" onClick={() => void ds.refetch()}>
              {t('chung.thuLai')}
            </Button>
          }
        >
          {t('baoCaoDoanhNghiep.nguon.loiTai', { loi: dichLoi(ds.error) })}
        </DaiBao>
      ) : ds.isPending ? (
        <Tile>
          <div className="space-y-2 p-5" aria-busy="true">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </Tile>
      ) : nguon.length === 0 ? (
        <Tile>
          <Empty
            icon={DatabaseOutlined}
            title={t('baoCaoDoanhNghiep.nguon.rong')}
            description={t('baoCaoDoanhNghiep.nguon.rongMoTa')}
            action={
              quyen.nguonTao ? (
                <Button variant="primary" size="md" icon={<PlusOutlined />} onClick={() => setHop({ loai: 'nguon' })}>
                  {t('baoCaoDoanhNghiep.nguon.themDauTien')}
                </Button>
              ) : undefined
            }
          />
        </Tile>
      ) : (
        <Tile className="min-w-0">
          <DataTable<Source>
            caption={tieuDe}
            dataSource={nguon}
            pagination={false}
            rowKey={(n) => n.id}
            columns={[
              {
                title: t('baoCaoDoanhNghiep.nguon.cotTen'),
                key: 'name',
                render: (_, n) => (
                  <div className="flex min-w-0 max-w-md flex-col gap-0.5">
                    <span className="line-clamp-2 text-sm font-medium text-content-primary">{n.name}</span>
                    <code className="font-mono text-2xs text-content-muted">{n.code}</code>
                    {n.description && <span className="line-clamp-1 text-xs text-content-secondary">{n.description}</span>}
                  </div>
                ),
              },
              {
                title: t('baoCaoDoanhNghiep.nguon.cotTaiLieu'),
                key: 'documentsTotal',
                width: 110,
                align: 'right',
                render: (_, n) => <span className="tabular-nums">{soVN(n.documentsTotal, locale)}</span>,
              },
              {
                title: t('baoCaoDoanhNghiep.nguon.cotCachNhap'),
                key: 'isManual',
                width: 140,
                render: (_, n) => (
                  <span className="whitespace-nowrap text-xs text-content-secondary">
                    {t(n.isManual ? 'baoCaoDoanhNghiep.nguon.nhapTay' : 'baoCaoDoanhNghiep.nguon.heThongNgoai')}
                  </span>
                ),
              },
              {
                title: t('chung.trangThai'),
                key: 'status',
                width: 170,
                render: (_, n) => {
                  const tt = TRANG_THAI_NGUON[n.isActive ? 'hoatDong' : 'dungHoatDong'];
                  return (
                    <span className="flex flex-wrap gap-1">
                      <Badge variant={tt.variant} className="whitespace-nowrap">{t(tt.khoa)}</Badge>
                      {n.isDefault && (
                        <Badge variant={BADGE_MAC_DINH.variant} className="whitespace-nowrap">
                          <CheckCircleOutlined aria-hidden />
                          {t(BADGE_MAC_DINH.khoa)}
                        </Badge>
                      )}
                    </span>
                  );
                },
              },
              {
                title: t('baoCaoDoanhNghiep.nguon.cotCapNhat'),
                key: 'updatedAt',
                width: 150,
                render: (_, n) => <span className="whitespace-nowrap text-xs tabular-nums">{ngayGioVN(n.updatedAt, locale)}</span>,
              },
              {
                title: <span className="sr-only">{t('chung.thaoTac')}</span>,
                key: 'thaoTac',
                width: 72,
                align: 'center',
                render: (_, n) => (
                  <MenuThaoTac
                    tenBanGhi={n.name}
                    muc={[
                      { khoa: 'sua', nhan: t('baoCaoDoanhNghiep.nguon.sua'), icon: <EditOutlined />, hien: quyen.nguonSua,
                        onChon: () => setHop({ loai: 'nguon', goc: n }) },
                      {
                        khoa: 'mac-dinh',
                        nhan: t('baoCaoDoanhNghiep.nguon.datMacDinh'),
                        icon: <StarOutlined />,
                        hien: quyen.nguonSua && !n.isDefault,
                        // Có quyền nhưng trạng thái dòng chặn: hiện mờ kèm lý do (ADR-UI-0005).
                        voHieu: !n.isActive,
                        ...(!n.isActive ? { moTa: t('baoCaoDoanhNghiep.nguon.batLaiTruoc') } : {}),
                        onChon: () => setHop({ loai: 'mac-dinh', nguon: n }),
                      },
                      n.isActive
                        ? { khoa: 'ngung', nhan: t('baoCaoDoanhNghiep.nguon.ngung'), icon: <PauseCircleOutlined />, nhom: 'pha-huy' as const,
                          hien: quyen.nguonSua && !n.isDefault, onChon: () => setHop({ loai: 'ngung', nguon: n }) }
                        : { khoa: 'bat', nhan: t('baoCaoDoanhNghiep.nguon.batLai'), icon: <PlayCircleOutlined />,
                          hien: quyen.nguonSua, onChon: () => batLai(n) },
                    ]}
                  />
                ),
              },
            ]}
          />
        </Tile>
      )}

      <KhoiTichHop maNguon={nguon.find((n) => n.isDefault)?.code} />

      {hop?.loai === 'nguon' && (
        <HopNguon key={hop.goc?.id ?? 'moi'} {...(hop.goc ? { goc: hop.goc } : {})} daCo={nguon.map((n) => n.code)} onClose={dongHop} />
      )}

      {hop?.loai === 'mac-dinh' && (
        <HopXacNhanThaoTac
          open
          tieuDe={t('baoCaoDoanhNghiep.nguon.macDinhTieuDe', { ten: hop.nguon.name })}
          than={t('baoCaoDoanhNghiep.nguon.macDinhThan')}
          nhanXacNhan={t('baoCaoDoanhNghiep.nguon.datMacDinh')}
          dangChay={datMacDinh.isPending}
          loi={datMacDinh.error ? dichLoi(datMacDinh.error) : null}
          onClose={() => {
            datMacDinh.reset();
            dongHop();
          }}
          onXacNhan={() =>
            datMacDinh.mutate(hop.nguon.id, {
              onSuccess: () => {
                toast.success(t('baoCaoDoanhNghiep.nguon.daDatMacDinh', { ten: hop.nguon.name }));
                dongHop();
              },
            })
          }
        />
      )}

      {hop?.loai === 'ngung' && (
        <HopXacNhanThaoTac
          open
          tieuDe={t('baoCaoDoanhNghiep.nguon.ngungTieuDe', { ten: hop.nguon.name })}
          than={t('baoCaoDoanhNghiep.nguon.ngungThan')}
          nhanXacNhan={t('baoCaoDoanhNghiep.nguon.ngung')}
          nguyHiem
          dangChay={datTrangThai.isPending}
          loi={datTrangThai.error ? dichLoi(datTrangThai.error) : null}
          onClose={() => {
            datTrangThai.reset();
            dongHop();
          }}
          onXacNhan={() =>
            datTrangThai.mutate(
              { id: hop.nguon.id, bat: false },
              {
                onSuccess: () => {
                  toast.success(t('baoCaoDoanhNghiep.nguon.daNgung', { ten: hop.nguon.name }));
                  dongHop();
                },
              },
            )
          }
        />
      )}
    </div>
  );
}

/**
 * Thêm nguồn (mã suy từ tên, chỉ để xem — không đổi được sau khi tạo) hoặc sửa tên, mô tả. Mã trùng do
 * máy chủ báo (`409 BAO_CAO_SOURCE_CODE_EXISTS`) hiện dưới dòng mã.
 */
function HopNguon({ goc, daCo, onClose }: { goc?: Source; daCo: string[]; onClose: () => void }) {
  const { t } = useT();
  const toast = useToast();
  const dichLoi = useLoiBaoCao();
  const formId = useId();
  const tao = useTaoNguon();
  const sua = useSuaNguon();
  const dang = goc ? sua : tao;

  const [ten, setTen] = useState(goc?.name ?? '');
  const [moTa, setMoTa] = useState(goc?.description ?? '');
  const [nhapTay, setNhapTay] = useState(true);
  const [daGui, setDaGui] = useState(false);

  const ma = goc ? goc.code : maNguonDuyNhat(ten, daCo);
  const truong = loiTheoTruong(dang.error);
  const loiTen = daGui && ten.trim() === ''
    ? t('baoCaoDoanhNghiep.loi.batBuoc')
    : daGui && !goc && ma === ''
      ? t('baoCaoDoanhNghiep.nguon.tenKhongSinhDuocMa')
      : truong['/name'];
  const loiMa = dang.error?.code === 'BAO_CAO_SOURCE_CODE_EXISTS' ? dang.error.message || dichLoi(dang.error) : truong['/code'];
  const loiChung = dang.error && !loiMa && Object.keys(truong).length === 0 ? dichLoi(dang.error) : null;

  const doi = (f: () => void) => {
    f();
    dang.reset();
  };

  const gui = () => {
    setDaGui(true);
    const tenGon = ten.trim();
    if (!tenGon || (!goc && ma === '') || dang.isPending) return;
    const moTaGon = moTa.trim();
    if (goc) {
      sua.mutate(
        { id: goc.id, du: { name: tenGon, ...(moTaGon !== (goc.description ?? '') ? { description: moTaGon } : {}) } },
        {
          onSuccess: (n) => {
            toast.success(t('baoCaoDoanhNghiep.nguon.daLuu', { ten: n.name }));
            onClose();
          },
        },
      );
    } else {
      tao.mutate(
        { code: ma, name: tenGon, ...(moTaGon ? { description: moTaGon } : {}), isManual: nhapTay },
        {
          onSuccess: (n) => {
            toast.success(t('baoCaoDoanhNghiep.nguon.daThem', { ten: n.name }));
            onClose();
          },
        },
      );
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t(goc ? 'baoCaoDoanhNghiep.nguon.suaTieuDe' : 'baoCaoDoanhNghiep.nguon.themTieuDe')}
      width={520}
      destroyOnHidden
      footer={
        <div className="space-y-3">
          {loiChung && <LoiTrongHop>{loiChung}</LoiTrongHop>}
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              {t('chung.huy')}
            </Button>
            <Button variant="primary" size="md" htmlType="submit" form={formId} loading={dang.isPending}>
              {dang.isPending ? t('chung.dangLuu') : t(goc ? 'baoCaoDoanhNghiep.nguon.luu' : 'baoCaoDoanhNghiep.nguon.them')}
            </Button>
          </div>
        </div>
      }
    >
      <form
        id={formId}
        noValidate
        className="space-y-4 pt-2"
        onSubmit={(e) => {
          e.preventDefault();
          gui();
        }}
      >
        <Field label={t('baoCaoDoanhNghiep.nguon.ten')} required error={loiTen}>
          {(p) => (
            <Input
              {...p}
              autoFocus
              maxLength={120}
              value={ten}
              placeholder={t('baoCaoDoanhNghiep.nguon.tenGoiY')}
              onChange={(e) => doi(() => setTen(e.target.value))}
            />
          )}
        </Field>

        {/* Mã để xem, không để sửa: nó đi vào `?source=` của app và cấu hình của hệ thống ngoài. */}
        <div className="space-y-1">
          <p className="text-sm text-content-secondary">
            {t(goc ? 'baoCaoDoanhNghiep.nguon.maLa' : 'baoCaoDoanhNghiep.nguon.maSeLa')}{' '}
            <code className="font-mono text-content-primary">{ma || '—'}</code>
          </p>
          <p className="text-xs text-content-muted">{t('baoCaoDoanhNghiep.nguon.maKhongDoiDuoc')}</p>
          {loiMa && (
            <p role="alert" className="text-xs text-danger-600">
              {loiMa}
            </p>
          )}
        </div>

        <Field label={t('baoCaoDoanhNghiep.nguon.moTaTruong')} error={truong['/description']}>
          {(p) => <Textarea {...p} rows={2} maxLength={500} value={moTa} onChange={(e) => doi(() => setMoTa(e.target.value))} />}
        </Field>

        {!goc && (
          <Checkbox checked={nhapTay} onChange={(e) => doi(() => setNhapTay(e.target.checked))}>
            {t('baoCaoDoanhNghiep.nguon.laNhapTay')}
          </Checkbox>
        )}
      </form>
    </Dialog>
  );
}

/**
 * Hướng dẫn cho hệ thống ngoài đẩy số liệu vào (thay màn khoá API riêng của native). Liên kết sang màn Khoá
 * API chỉ hiện khi người dùng vào được màn đó (phân hệ `API_KEY`); không thì nói ai làm được.
 */
function KhoiTichHop({ maNguon }: { maNguon: string | undefined }) {
  const { t } = useT();
  const duongDan = useDuongDan();
  const coKhoaApi = usePermission(PHAN_HE_KHOA_API);
  const nguonVd = maNguon ?? 'default';
  const goc = `${typeof window === 'undefined' ? '' : window.location.origin}${GOC_CONG_KHAI}`;

  const dong: Array<{ phuongThuc: string; duong: string; khoa: 'dayPhanHe' | 'dayTaiLieu' | 'doiSoat' | 'goTaiLieu' }> = [
    { phuongThuc: 'PUT', duong: `/modules/{moduleId}?source=${nguonVd}`, khoa: 'dayPhanHe' },
    { phuongThuc: 'PUT', duong: `/documents?source=${nguonVd}`, khoa: 'dayTaiLieu' },
    { phuongThuc: 'GET', duong: `/documents?source=${nguonVd}&limit=100`, khoa: 'doiSoat' },
    { phuongThuc: 'DELETE', duong: `/documents/{externalId}?source=${nguonVd}`, khoa: 'goTaiLieu' },
  ];

  return (
    <Tile>
      <TileHeader icon={<ApiOutlined />} tone="warning" title={t('baoCaoDoanhNghiep.tichHop.tieuDe')} mo={t('baoCaoDoanhNghiep.tichHop.moTa')} />
      <TileBody className="space-y-4">
        <dl className="grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-[max-content_1fr]">
          <dt className="text-content-muted">{t('baoCaoDoanhNghiep.tichHop.diaChiGoc')}</dt>
          <dd className="min-w-0 break-all font-mono text-xs text-content-primary">{goc}</dd>
          <dt className="text-content-muted">{t('baoCaoDoanhNghiep.tichHop.xacThuc')}</dt>
          <dd className="font-mono text-xs text-content-primary">X-Api-Key: &lt;API_KEY&gt;</dd>
          <dt className="text-content-muted">{t('baoCaoDoanhNghiep.tichHop.chonNguon')}</dt>
          <dd className="text-xs text-content-secondary">{t('baoCaoDoanhNghiep.tichHop.chonNguonMo')}</dd>
        </dl>

        <ul className="space-y-2">
          {dong.map((d) => (
            <li key={d.duong + d.phuongThuc} className="rounded border border-default px-3 py-2">
              <p className="flex min-w-0 flex-wrap items-baseline gap-2 font-mono text-xs">
                <span className="font-semibold text-content-primary">{d.phuongThuc}</span>
                <span className="min-w-0 break-all text-content-secondary">{d.duong}</span>
              </p>
              <p className="mt-0.5 text-xs text-content-muted">{t(`baoCaoDoanhNghiep.tichHop.${d.khoa}`)}</p>
            </li>
          ))}
        </ul>

        <p className="text-xs leading-relaxed text-content-secondary">
          {t('baoCaoDoanhNghiep.tichHop.phamVi')}{' '}
          <code className="font-mono text-content-primary">bao-cao:tai-chinh:update</code>,{' '}
          <code className="font-mono text-content-primary">bao-cao:tai-lieu:import</code>
        </p>

        {coKhoaApi ? (
          <Link
            to={duongDan('/he-thong/khoa-api')}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:underline"
          >
            <ApiOutlined aria-hidden />
            {t('baoCaoDoanhNghiep.tichHop.moKhoaApi')}
          </Link>
        ) : (
          <p className="text-xs text-content-muted">{t('baoCaoDoanhNghiep.tichHop.nhoQuanTri')}</p>
        )}
      </TileBody>
    </Tile>
  );
}
